import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, ShieldAlert, PhoneCall } from "lucide-react";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Field, Textarea, Select } from "@/components/kit";
import { AddressLookup } from "@/components/AddressLookup";
import { useAsync } from "@/lib/hooks";
import { loadDriverContext, createBooking } from "@/lib/driverData";
import { getDriverContact, tel } from "@/lib/driverContact";

const FAULTS = [
  { value: "wont_start", label: "Won't start" },
  { value: "warning_light", label: "Warning light / dashboard alert" },
  { value: "tyre", label: "Tyre / wheel" },
  { value: "overheating", label: "Overheating / smoke" },
  { value: "accident_damage", label: "Accident damage" },
  { value: "brakes", label: "Brakes / steering" },
  { value: "locked_out", label: "Locked out / keys" },
  { value: "other", label: "Something else" },
];

function CallRow({ testId, label, sub, number, tone = "outline" }) {
  const styles = {
    red: "bg-[hsl(var(--vda-red))] text-white",
    primary: "bg-primary text-primary-foreground",
    outline: "border border-[hsl(var(--input))] text-foreground",
  };
  const disabled = !number;
  return (
    <a href={disabled ? undefined : `tel:${tel(number)}`} data-testid={testId} aria-disabled={disabled}
      className={`flex items-center justify-between h-14 px-4 rounded-[8px] ${styles[tone]} ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <span className="flex items-center gap-2.5">
        <PhoneCall className="h-4 w-4" />
        <span className="text-left leading-tight">
          <span className="block text-sm font-semibold">{label}</span>
          {sub && <span className="block text-[11px] opacity-80">{sub}</span>}
        </span>
      </span>
      <span className="vda-reg text-sm">{number || "—"}</span>
    </a>
  );
}

export default function DriverBreakdown() {
  const nav = useNavigate();
  const { data, loading } = useAsync(async () => {
    const ctx = await loadDriverContext();
    const contact = await getDriverContact();
    return { ctx, contact };
  }, []);
  const [desc, setDesc] = useState("");
  const [fault, setFault] = useState("wont_start");
  const [addr, setAddr] = useState({});
  const [safe, setSafe] = useState("no");
  const [showRecord, setShowRecord] = useState(false);
  const [saving, setSaving] = useState(false);

  if (loading) return <DriverShell back="/driver"><Spinner /></DriverShell>;
  const { ctx, contact } = data;

  const submitRecord = async () => {
    setSaving(true);
    try {
      const id = await createBooking(ctx, {
        request_type: "breakdown", request_category: "breakdown",
        request_details: {
          flow: "breakdown", call_first: true,
          fault, fault_label: FAULTS.find((f) => f.value === fault)?.label,
          location: addr?.address_text ? addr : undefined,
        },
        description: desc || null,
        safe_to_drive: safe === "yes",
        recovery_required: safe !== "yes",
        location_text: addr?.address_text || null, postcode: addr?.postcode || null,
        priority: "high",
      });
      toast.success("Supporting record created for your Venson team.");
      nav(`/driver/booking/${id}`, { replace: true });
    } catch (err) {
      toast.error(err.message || "Could not create the record.");
    } finally { setSaving(false); }
  };

  return (
    <DriverShell back="/driver" title="Urgent assistance">
      <Panel className="p-5 bg-[hsl(var(--vda-red))]/8 border-[hsl(var(--vda-red))]/30" data-testid="emergency-panel">
        <div className="flex items-center gap-2 text-[hsl(var(--vda-red))]">
          <ShieldAlert className="h-5 w-5" />
          <span className="font-semibold">In a breakdown or emergency, call first</span>
        </div>
        <p className="mt-2 text-sm text-foreground/80">
          Relay logs your request, but it does not summon assistance. If anyone is hurt or in danger, call <strong>999</strong>. Otherwise call the right number below and someone will help you straight away.
        </p>
      </Panel>

      <div className="mt-5 space-y-2">
        <span className="vda-tick text-muted-foreground">Quick call</span>
        <CallRow testId="emergency-999" label="Emergency services" sub="Injury or danger" number="999" tone="red" />
        {contact?.emergency_phone && (
          <CallRow testId="emergency-customer-line" label={`${contact.customer_name || "Customer"} emergency line`} sub="Out-of-hours assistance" number={contact.emergency_phone} tone="primary" />
        )}
        <CallRow testId="emergency-aa-fleet" label={contact?.aa_fleet_label || "AA Fleet Assistance"} sub="Roadside recovery" number={contact?.aa_fleet_phone} tone="outline" />
        <CallRow testId="emergency-support-line" label="Driver support" sub={contact?.customer_name || "Your Venson team"} number={contact?.support_phone} tone="outline" />
      </div>

      <div className="mt-7 border-t border-border pt-5">
        {!showRecord ? (
          <button type="button" data-testid="breakdown-log-toggle" onClick={() => setShowRecord(true)}
            className="w-full text-left">
            <h2 className="font-semibold flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> Already spoke to someone?</h2>
            <p className="text-sm text-muted-foreground mt-1">Log a supporting record so your Venson team has the details. This does not summon assistance.</p>
          </button>
        ) : (
          <div className="space-y-4">
            <h2 className="font-semibold">Log a supporting record</h2>
            <p className="text-sm text-muted-foreground -mt-2">Give us the essentials so we can help fast. Your mobile and mileage are already on file.</p>
            <Field label="What has gone wrong?">
              <Select data-testid="breakdown-fault" value={fault} onChange={(e) => setFault(e.target.value)}>
                {FAULTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </Select>
            </Field>
            <Field label="Is the vehicle safe to drive?">
              <div className="grid grid-cols-2 gap-3">
                {[["no", "No / not sure"], ["yes", "Yes"]].map(([v, label]) => (
                  <button key={v} type="button" data-testid={`breakdown-safe-${v}`} onClick={() => setSafe(v)}
                    className={`h-11 rounded-[6px] border text-sm font-medium ${safe === v ? (v === "no" ? "border-[hsl(var(--vda-red))] bg-[hsl(var(--vda-red))]/10 text-[hsl(var(--vda-red))]" : "border-primary bg-primary/10 text-primary") : "border-[hsl(var(--input))]"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <AddressLookup label="Exact vehicle location" value={addr} onChange={setAddr} testIdPrefix="breakdown-address" />
            <Field label="Anything else? (optional)">
              <Textarea data-testid="breakdown-desc" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Landmarks, symptoms, third parties" />
            </Field>
            <Btn onClick={submitRecord} loading={saving} data-testid="breakdown-submit" className="w-full">Create record</Btn>
          </div>
        )}
      </div>
    </DriverShell>
  );
}
